/**
 * One Studio project in the list on /studio.
 *
 * Built as PlaylistCard is: a role="button" container with real buttons inside it, each
 * stopping propagation. Same reason — nesting a <button> inside a <button> is invalid HTML
 * that browsers resolve unpredictably — and the same consequence: pressing "Open in
 * Studio" must not also fire the row's own open.
 *
 * THERE IS NO PLAY BUTTON, and that is the difference from PlaylistCard rather than an
 * omission. A playlist is a listening sequence, so playing it is the point of the row; a
 * project is a working set the Studio opens one track at a time (see the `StudioProject`
 * type), so it has no queue to start and nothing to report about playback. A play control
 * here would be a second, quieter way of starting the player from a surface that owns no
 * playback state.
 *
 * "OPEN IN STUDIO" IS SPELLED OUT rather than being an arrow or an implicit row click,
 * because Batch 6 asks for the Studio to be entered deliberately. The row is still
 * clickable — it does the same thing — so the explicit button is the label for the
 * gesture, not the only way to perform it.
 */
import { FolderOpen, Music2, Pencil, Trash2 } from 'lucide-react';
import { ActionMenu, Btn } from '../ui';
import { c } from '../../styles/theme';
import type { Song, StudioProject } from '../../types/domain';

export interface ProjectCardProps {
  project: StudioProject;
  /** The project's songs, resolved against the library and in the order added. */
  songs: Song[];
  /** "3 tracks · whole library". */
  summary: string;
  /**
   * True when this is the project the Studio is currently working in.
   *
   * Drawn as the accent border every "the thing you are on" surface in the app uses, and
   * announced with aria-current so it is not colour-only.
   */
  current?: boolean;
  onOpen: (project: StudioProject) => void;
  onRename: (project: StudioProject) => void;
  onDelete: (project: StudioProject) => void;
}

export default function ProjectCard({
  project,
  songs,
  summary,
  current = false,
  onOpen,
  onRename,
  onDelete,
}: ProjectCardProps) {
  const empty = songs.length === 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onOpen(project)}
      onKeyDown={(e) => {
        // Space activates a role="button" natively and would otherwise scroll.
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(project);
        }
      }}
      aria-label={`Open project ${project.name} in the Studio`}
      aria-current={current ? 'true' : undefined}
      className="lyx-track-row"
      style={{
        border: `1px solid ${current ? c.accentBorder : c.border}`,
        borderRadius: 12,
        background: c.surface1,
        padding: 12,
        cursor: 'pointer',
      }}
    >
      {/* The flex is a class rather than an inline style for the reason .lyx-release-row
          states about the identical row in My Releases: it has to wrap on a phone, and an
          inline `display: flex` cannot be given `flex-wrap` at a breakpoint. At 380px the
          folder tile, "Open in Studio" and the More trigger leave the project's name about
          70px, and the button was the half that would not give. */}
      <div className="lyx-project-row">
        <div
          aria-hidden="true"
          style={{
            width: 44,
            height: 44,
            borderRadius: 10,
            flexShrink: 0,
            background: c.surface2,
            border: `1px solid ${c.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <FolderOpen size={17} color={current ? c.accent : c.text3} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <p
            style={{
              fontWeight: 500,
              fontSize: 13,
              margin: '0 0 3px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {project.name}
          </p>
          <p
            style={{
              fontSize: 11,
              color: c.text2,
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
            }}
          >
            <Music2 size={11} color={c.text3} aria-hidden="true" />
            {summary}
          </p>
        </div>

        <div
          className="lyx-row-controls"
          style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Btn
            variant="secondary"
            /*
              Disabled on a project with no resolvable tracks rather than hidden: the
              workspace works on a song and there is none to open, so the button states
              that instead of moving out of the way. The hook refuses it anyway and says
              why, so this is belt and braces on the same rule.
            */
            disabled={empty}
            // No stopPropagation of its own: the wrapper above already stops clicks from
            // reaching the row, which is the same arrangement PlaylistCard's controls use.
            onClick={() => onOpen(project)}
            style={{ height: 30 }}
          >
            Open in Studio
          </Btn>

          <ActionMenu
            label={`More actions for ${project.name}`}
            stopPropagation
            triggerStyle={{ width: 30, height: 30 }}
            items={[
              {
                label: 'Rename',
                icon: <Pencil size={13} />,
                onSelect: () => onRename(project),
              },
              {
                label: 'Delete project',
                icon: <Trash2 size={13} />,
                danger: true,
                onSelect: () => onDelete(project),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
}
